import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const expiryTimerRef = useRef(null);

  const isTokenExpired = (decoded) => {
    if (!decoded.exp) return true;
    return decoded.exp < Date.now() / 1000;
  };

  // Limpia el temporizador anterior si lo hubiera
  const clearExpiryTimer = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  };

  const logout = useCallback((expired = false) => {
    clearExpiryTimer();
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    if (expired) setSessionExpired(true);
  }, []);

  // Programa el auto-logout justo cuando vence el token
  const scheduleExpiry = useCallback((decoded) => {
    clearExpiryTimer();
    const msUntilExpiry = decoded.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) return;
    expiryTimerRef.current = setTimeout(() => logout(true), msUntilExpiry);
  }, [logout]);

  // Comprueba el token almacenado (al montar y al recuperar el foco)
  const checkStoredToken = useCallback(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) return;
    try {
      const decoded = jwtDecode(storedToken);
      if (isTokenExpired(decoded)) {
        logout(true);
      }
    } catch {
      logout(true);
    }
  }, [logout]);

  // Al montar: carga el token si existe
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      try {
        const decoded = jwtDecode(storedToken);
        if (isTokenExpired(decoded)) {
          logout(true);
        } else {
          setToken(storedToken);
          setUser({ id: decoded.sub, rol: decoded.rol });
          scheduleExpiry(decoded);
        }
      } catch {
        logout(true);
      }
    }
    setLoading(false);
  }, []); // eslint-disable-line

  // Al recuperar el foco (p.ej. el usuario vuelve de otra pestaña o desbloquea el PC)
  useEffect(() => {
    window.addEventListener("focus", checkStoredToken);
    return () => window.removeEventListener("focus", checkStoredToken);
  }, [checkStoredToken]);

  const login = (newToken) => {
    try {
      const decoded = jwtDecode(newToken);
      if (isTokenExpired(decoded)) return false;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser({ id: decoded.sub, rol: decoded.rol });
      setSessionExpired(false);
      scheduleExpiry(decoded);
      return true;
    } catch {
      return false;
    }
  };

  // Redirige al login cuando la sesión ha caducado
  useEffect(() => {
    if (sessionExpired) {
      window.location.href = "/?expired=1";
    }
  }, [sessionExpired]);

  return (
    <AuthContext.Provider
      value={{ token, user, setUser, loading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
